import { DOCUMENT } from '@angular/common';
import {
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  NgModule,
  Optional,
} from '@angular/core';
import { EVENT_MANAGER_PLUGINS, EventManagerPlugin } from '@angular/platform-browser';

/**
 * Local HammerJS ↔ Angular EventManager bridge.
 * Angular 22 removed the built-in Hammer integration; this keeps swipe/press bindings working.
 */

declare const Hammer: any;

const EVENT_NAMES: Record<string, boolean> = {
  pan: true,
  panstart: true,
  panmove: true,
  panend: true,
  pancancel: true,
  panleft: true,
  panright: true,
  panup: true,
  pandown: true,
  pinch: true,
  pinchstart: true,
  pinchmove: true,
  pinchend: true,
  pinchcancel: true,
  pinchin: true,
  pinchout: true,
  press: true,
  pressup: true,
  rotate: true,
  rotatestart: true,
  rotatemove: true,
  rotateend: true,
  rotatecancel: true,
  swipe: true,
  swipeleft: true,
  swiperight: true,
  swipeup: true,
  swipedown: true,
  tap: true,
  doubletap: true,
};

export const HAMMER_GESTURE_CONFIG = new InjectionToken<HammerGestureConfig>('HammerGestureConfig');

export type HammerLoader = () => Promise<void>;

export const HAMMER_LOADER = new InjectionToken<HammerLoader>('HammerLoader');

export interface HammerInstance {
  on(eventName: string, callback?: Function): void;
  off(eventName: string, callback?: Function): void;
  destroy?(): void;
}

@Injectable()
export class HammerGestureConfig {
  events: string[] = [];
  overrides: { [key: string]: Object } = {};
  options?: {
    cssProps?: any;
    domEvents?: boolean;
    enable?: boolean | ((manager: any) => boolean);
    preset?: any[];
    touchAction?: string;
    recognizers?: any[];
    inputClass?: any;
    inputTarget?: EventTarget;
  };

  buildHammer(element: HTMLElement): HammerInstance {
    const mc = new Hammer(element, this.options);

    mc.get('pinch').set({ enable: true });
    mc.get('rotate').set({ enable: true });

    for (const eventName in this.overrides) {
      if (Object.prototype.hasOwnProperty.call(this.overrides, eventName)) {
        mc.get(eventName).set(this.overrides[eventName]);
      }
    }

    return mc;
  }
}

@Injectable()
export class HammerConfig extends HammerGestureConfig {
  public override options = {
    touchAction: 'pan-y',
  };
}

@Injectable()
export class HammerGesturesPlugin extends EventManagerPlugin {
  private _loaderPromise: Promise<void> | null = null;

  constructor(
    @Inject(DOCUMENT) doc: any,
    @Inject(HAMMER_GESTURE_CONFIG) private _config: HammerGestureConfig,
    private _injector: Injector,
    @Optional() @Inject(HAMMER_LOADER) private loader?: HammerLoader | null,
  ) {
    super(doc);
  }

  override supports(eventName: string): boolean {
    if (!EVENT_NAMES.hasOwnProperty(eventName.toLowerCase()) && !this.isCustomEvent(eventName)) {
      return false;
    }

    if (!(window as any).Hammer && !this.loader) {
      console.warn(
        `The "${eventName}" event cannot be bound because Hammer.JS is not ` +
          `loaded and no custom loader has been specified.`,
      );
      return false;
    }

    return true;
  }

  override addEventListener(element: HTMLElement, eventName: string, handler: Function): Function {
    const zone = this.manager.getZone();
    eventName = eventName.toLowerCase();

    if (!(window as any).Hammer && this.loader) {
      this._loaderPromise = this._loaderPromise || zone.runOutsideAngular(() => this.loader!());
      let cancelRegistration = false;
      let deregister: Function = () => {
        cancelRegistration = true;
      };

      zone.runOutsideAngular(() =>
        this._loaderPromise!
          .then(() => {
            if (!(window as any).Hammer) {
              console.warn(`The custom HAMMER_LOADER completed, but Hammer.JS is not present.`);
              deregister = () => {};
              return;
            }

            if (!cancelRegistration) {
              deregister = this.addEventListener(element, eventName, handler);
            }
          })
          .catch(() => {
            console.warn(
              `The "${eventName}" event cannot be bound because the custom Hammer.JS loader failed.`,
            );
            deregister = () => {};
          }),
      );

      return () => {
        deregister();
      };
    }

    return zone.runOutsideAngular(() => {
      const mc = this._config.buildHammer(element);
      const callback = function (eventObj: any) {
        zone.runGuarded(function () {
          handler(eventObj);
        });
      };
      mc.on(eventName, callback);
      return () => {
        mc.off(eventName, callback);
        if (typeof mc.destroy === 'function') {
          mc.destroy();
        }
      };
    });
  }

  isCustomEvent(eventName: string): boolean {
    return this._config.events.indexOf(eventName) > -1;
  }
}

@NgModule({
  providers: [
    {
      provide: EVENT_MANAGER_PLUGINS,
      useClass: HammerGesturesPlugin,
      multi: true,
      deps: [DOCUMENT, HAMMER_GESTURE_CONFIG, Injector, [new Optional(), HAMMER_LOADER]],
    },
    { provide: HAMMER_GESTURE_CONFIG, useClass: HammerGestureConfig },
  ],
})
export class HammerModule {}
